// 首先加载 polyfill，确保在 React 之前执行
import 'core-js/stable';
import 'regenerator-runtime/runtime';

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

// 清理旧版本残留：移除 body/html 上的 no-flexbox 类和内联降级样式（避免被缓存的旧 index.html 注入影响样式）
(function removeLegacyFallback() {
  try {
    document.body.classList.remove('no-flexbox');
    document.documentElement.classList.remove('no-flexbox');
    const legacyStyleTags = Array.from(document.head.querySelectorAll('style'))
      .filter(tag => tag.textContent && (tag.textContent.includes('venue-dashboard-grid') || tag.textContent.includes('no-flexbox')));
    legacyStyleTags.forEach(tag => document.head.removeChild(tag));
  } catch (e) {
    console.warn('[Compatibility] Failed to remove legacy fallback', e);
  }
})();

// ===== 兼容性检测 =====
const checkBrowserCompatibility = function() {
  var checks = {
    promise: typeof Promise !== 'undefined',
    mapSet: typeof Map !== 'undefined' && typeof Set !== 'undefined',
    symbol: typeof Symbol !== 'undefined',
    fetch: typeof fetch !== 'undefined',
    cssVariables: (function() {
      try {
        return window.CSS && window.CSS.supports && window.CSS.supports('--test', '0');
      } catch (e) {
        return false;
      }
    })()
  };
  
  var isCompatible = checks.promise && checks.mapSet && checks.fetch;
  console.log('[Browser Check]', checks);
  return { isCompatible: isCompatible, checks: checks };
};

var compatResult = checkBrowserCompatibility();
var isCompatible = compatResult.isCompatible;
var checks = compatResult.checks;

// 如果不兼容，显示提示信息
if (!isCompatible) {
  console.error('[Compatibility Error] Browser does not support required features');
  var rootElement = document.getElementById('root');
  if (rootElement) {
    rootElement.innerHTML = 
      '<div style="padding: 20px; text-align: center; font-family: sans-serif;">' +
        '<h2>浏览器版本过低</h2>' +
        '<p>请升级您的系统或联系技术支持</p>' +
        '<p style="color: #666; font-size: 12px;">' +
          'Promise: ' + (checks.promise ? '✓' : '✗') + ' | ' +
          'Map/Set: ' + (checks.mapSet ? '✓' : '✗') + ' | ' +
          'Fetch: ' + (checks.fetch ? '✓' : '✗') +
        '</p>' +
      '</div>';
  }
}

// ===== 版本检测与自动更新 =====
(function setupVersionCheck() {
  // 从环境变量或配置中获取检测间隔（默认5分钟）
  var CHECK_INTERVAL = 5 * 60 * 1000; // 5分钟
  var currentVersion = null;
  var checkTimer = null;

  // 获取版本信息（带缓存破坏）
  function fetchVersion() {
    var cacheBuster = '_cb=' + Date.now();
    var versionUrl = (process.env.PUBLIC_URL || '') + '/version.json?' + cacheBuster;
    
    return fetch(versionUrl, {
      method: 'GET',
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    }).then(function(response) {
      if (!response.ok) {
        throw new Error('Failed to fetch version: ' + response.status);
      }
      return response.json();
    });
  }

  // 检查是否有新版本
  function checkForUpdate() {
    fetchVersion().then(function(data) {
      if (!data || !data.version) {
        console.warn('[Version Check] Invalid version data');
        return;
      }

      // 首次加载，记录当前版本
      if (!currentVersion) {
        currentVersion = data.version;
        console.log('[Version Check] Current version:', currentVersion);
        return;
      }

      // 检测到新版本
      if (data.version !== currentVersion) {
        console.log('[Version Check] New version detected:', data.version, '(was:', currentVersion + ')');
        handleNewVersion(data);
      }
    }).catch(function(error) {
      console.warn('[Version Check] Error checking version:', error.message);
    });
  }

  // 处理新版本
  function handleNewVersion(versionData) {
    // 创建提示弹窗
    var overlay = document.createElement('div');
    overlay.id = 'version-update-overlay';
    overlay.style.cssText = 
      'position: fixed; top: 0; left: 0; right: 0; bottom: 0; ' +
      'background: rgba(0,0,0,0.6); z-index: 99999; ' +
      'display: flex; align-items: center; justify-content: center;';

    var dialog = document.createElement('div');
    dialog.style.cssText = 
      'background: white; padding: 24px; border-radius: 8px; ' +
      'max-width: 360px; text-align: center; box-shadow: 0 4px 12px rgba(0,0,0,0.3);';

    dialog.innerHTML = 
      '<h3 style="margin: 0 0 12px 0; color: #333; font-size: 18px;">发现新版本</h3>' +
      '<p style="margin: 0 0 20px 0; color: #666; font-size: 14px; line-height: 1.5;">' +
        '系统已更新，建议您刷新页面以获取最新功能和修复。' +
      '</p>' +
      '<div style="display: flex; gap: 12px; justify-content: center;">' +
        '<button id="version-update-later" style="padding: 8px 20px; border: 1px solid #d9d9d9; ' +
          'background: white; border-radius: 4px; cursor: pointer; font-size: 14px;">稍后</button>' +
        '<button id="version-update-now" style="padding: 8px 20px; border: none; ' +
          'background: #1890ff; color: white; border-radius: 4px; cursor: pointer; font-size: 14px;">' +
          '立即刷新' +
        '</button>' +
      '</div>';

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // 绑定按钮事件
    document.getElementById('version-update-later').onclick = function() {
      document.body.removeChild(overlay);
      console.log('[Version Check] User chose to update later');
    };

    document.getElementById('version-update-now').onclick = function() {
      console.log('[Version Check] User chose to update now');
      window.location.reload(true); // 强制刷新，跳过缓存
    };
  }

  // 启动定时检测
  function startVersionCheck() {
    // 首次检测（延迟10秒，避免影响页面加载）
    setTimeout(function() {
      checkForUpdate();
      // 设置定时检测
      checkTimer = setInterval(checkForUpdate, CHECK_INTERVAL);
    }, 10000);
  }

  // 页面可见性变化时立即检测
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible') {
      // 页面重新可见时立即检测
      setTimeout(checkForUpdate, 1000);
    }
  });

  // 启动检测
  if (typeof window !== 'undefined' && window.fetch) {
    startVersionCheck();
    console.log('[Version Check] Started (interval: ' + (CHECK_INTERVAL / 60000) + ' minutes)');
  }
})();

// ===== React 渲染 =====
var container = document.getElementById('root');

// 使用 React 18 createRoot
var root = ReactDOM.createRoot(container);
root.render(
  React.createElement(React.StrictMode, null,
    React.createElement(App, null)
  )
);
console.log('[React] Application rendered');
